import { DynamicSizedLoadingRow } from 'components/Loader/styled'
import Pagination, { SearchResultStats } from 'components/PaginationControls'
import V2PositionListItem from 'components/PositionListItem/v2'
import { Spinner, SpinnerWithText } from 'components/Spinner'
import { V2_POSITIONS_PER_PAGE } from 'pages/Liquidity/ProtocolLists/v2/hooks/useUserV2Positions'
import React, { useMemo } from 'react'
import { V2PositionDetails } from 'state/data/slice'
import styled from 'styled-components'
import { StyledInternalLink, ThemedText } from 'theme'

import * as S from './styled'
import V2HeaderRow from './V2HeaderRow'

type V2PositionListProps = React.PropsWithChildren<{
  positions: V2PositionDetails[]
  account: any
  loading?: boolean
  totalCount?: number
  currentPage?: number
  totalPages?: number
  onPageChange?: (page: number) => void
  searchParam?: string | null
  isSearching?: boolean
  transitionLabel?: string
  showTransitionOverlay?: boolean
}>

const DataTransitionOverlay = styled.div`
  position: absolute;
  top: 58px;
  left: 0;
  right: 0;
  bottom: 0;

  background: ${({ theme }) => `${theme.cardPrimary}`};

  display: flex;
  align-items: center;
  justify-content: center;

  z-index: 200;

  border-radius: 16px;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);

  animation: fadeIn 200ms ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const v2PositionLoader = () => (
  <>
    <DynamicSizedLoadingRow
      height="30em"
      style={{ width: '100%' }}
      borderRadius="16px"
      borderTopLeftRadius="0px"
      borderTopRightRadius="0px"
      borderBottomLeftRadius="16px"
      borderBottomRightRadius="16px"
    >
      <div />
    </DynamicSizedLoadingRow>
  </>
)

export default function V2PositionList({
  positions,
  account,
  loading = false,
  totalCount = 0,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  searchParam,
  isSearching = false,
  transitionLabel,
  showTransitionOverlay = false,
}: V2PositionListProps) {
  const positionItems = useMemo(() => {
    return positions.map((position, index) => (
      <V2PositionListItem
        key={`${position.pair.liquidityToken.address}-${index}`}
        pair={position.pair}
        userPoolBalance={position.liquidityTokenBalance}
        totalSupply={position.totalSupply}
        valueUSD={position.valueUSD}
        row={index}
      />
    ))
  }, [positions])

  const displayStats = useMemo(() => {
    const startIndex = totalCount > 0 ? (currentPage - 1) * V2_POSITIONS_PER_PAGE + 1 : 0
    const endIndex = Math.min(currentPage * V2_POSITIONS_PER_PAGE, totalCount)
    return { startIndex, endIndex }
  }, [currentPage, totalCount])

  const getTransitionLabel = () => {
    if (transitionLabel) {
      return transitionLabel
    }

    if (searchParam) {
      const isAddressLike = /^0x[a-fA-F0-9]{40}$/.test(searchParam.trim())
      if (isAddressLike) {
        return `Searching for V2 token "${searchParam.slice(0, 6)}...${searchParam.slice(-4)}"...`
      }
      return `Searching V2 positions for "${searchParam}"...`
    }

    if (loading) {
      return 'Loading V2 positions...'
    }

    return 'Searching V2 positions...'
  }

  const shouldShowOverlay =
    showTransitionOverlay || (isSearching && !showTransitionOverlay) || (loading && !showTransitionOverlay)

  return (
    <S.MainContentWrapper>
      <S.TableWrapper>
        {shouldShowOverlay && (
          <DataTransitionOverlay>
            <SpinnerWithText>
              <Spinner />
              <ThemedText.DeprecatedBody color="white">{getTransitionLabel()}</ThemedText.DeprecatedBody>
            </SpinnerWithText>
          </DataTransitionOverlay>
        )}
        <S.Table>
          <V2HeaderRow />
          {shouldShowOverlay ? (
            <tbody>
              <tr>
                <td colSpan={7} style={{ height: '400px', width: '100%' }}></td>
              </tr>
            </tbody>
          ) : (
            <tbody>{positionItems}</tbody>
          )}
        </S.Table>

        {!shouldShowOverlay && totalPages > 1 && onPageChange && (
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
        )}

        {searchParam && !shouldShowOverlay && (
          <SearchResultStats>
            Showing {displayStats.startIndex}-{displayStats.endIndex} of {totalCount} V2 position
            {totalCount !== 1 ? 's' : ''}
            {searchParam && ` matching "${searchParam}"`}
          </SearchResultStats>
        )}
      </S.TableWrapper>

      {!shouldShowOverlay && (
        <S.PositionListFooter>
          <S.ResponsiveButtonPrimary
            id="find-pool-button"
            as={StyledInternalLink}
            to="/liquidity/v2/find"
            padding="6px 8px"
          >
            <>Import Pool</>
          </S.ResponsiveButtonPrimary>
          <S.ResponsiveButtonPrimary
            id="join-pool-button"
            as={StyledInternalLink}
            to="/liquidity/v2/add/BERA"
            padding="6px 8px"
          >
            <>Add V2 Liquidity</>
          </S.ResponsiveButtonPrimary>
        </S.PositionListFooter>
      )}
    </S.MainContentWrapper>
  )
}
